// blitzpaypagecomponents/CompactMetricCard.jsx - Clean Design + Tutorial Support
import React from "react";
import { Card, CardContent, Box, Typography, IconButton, useMediaQuery } from "@mui/material";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";

const CompactMetricCard = ({
  title,
  value,
  subtitle,
  icon,
  color = "primary",
  alert = false,
  onClick,
  dataTour, // NEW: Support for data-tour attribute
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Card
      data-tour={dataTour}
      sx={{
        height: "100%",
        minHeight: isMobile ? 'auto' : { md: 85, lg: 95 },
        borderRadius: { xs: 2, md: 2.5 },
        border: '1px solid',
        borderColor: alert ? `${color}.main` : 'divider',
        bgcolor: alert ? alpha(theme.palette[color].main, 0.04) : 'background.paper',
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s",
        position: "relative",
        overflow: "hidden",
        display: 'flex',
        flexDirection: 'column',
        '&:hover': onClick ? {
          borderColor: `${color}.main`,
          transform: "translateY(-2px)",
          boxShadow: 2,
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, pb: { xs: "16px !important", md: "20px !important" }, height: "100%", display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ 
          display: "flex", 
          flexDirection: "column",
          height: "100%",
        }}>
          {/* Header with Icon and Arrow */}
          <Box sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "flex-start",
            mb: { xs: 1.5, md: 1.5 },
          }}>
            <Box
              sx={{
                width: { xs: 36, md: 36 },
                height: { xs: 36, md: 36 },
                borderRadius: { xs: 1.5, md: 2 },
                bgcolor: alpha(theme.palette[color].main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {React.cloneElement(icon, { 
                sx: { fontSize: { xs: 20, md: 20 }, color: `${color}.main` }
              })}
            </Box>
            
            {onClick && (
              <IconButton
                size="small"
                sx={{
                  width: { xs: 24, md: 24 },
                  height: { xs: 24, md: 24 },
                  color: 'text.secondary',
                  opacity: 0.6,
                  '&:hover': {
                    opacity: 1,
                    bgcolor: alpha(theme.palette[color].main, 0.08),
                  },
                }}
              >
                <ChevronRightIcon sx={{ fontSize: { xs: 18, md: 18 } }} />
              </IconButton>
            )}
          </Box>

          {/* Title */}
          <Typography
            variant="caption"
            sx={{
              fontSize: { xs: "0.7rem", md: "0.7rem" },
              fontWeight: 600,
              color: 'text.secondary',
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: { xs: 0.5, md: 0.5 },
            }}
          >
            {title}
          </Typography>

          {/* Value */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.5rem", md: "1.5rem", lg: "1.75rem" },
              lineHeight: 1.1,
              mb: { xs: 0.5, md: 0.5 },
              wordBreak: "break-word",
            }}
          >
            {value}
          </Typography>

          {/* Subtitle */}
          {subtitle && (
            <Typography
              variant="caption"
              sx={{
                fontSize: { xs: "0.7rem", md: "0.7rem" },
                color: 'text.secondary',
                fontWeight: 500,
                mt: "auto",
              }}
            >
              {subtitle}
            </Typography>
          )}

          {/* Alert Indicator */}
          {alert && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                width: { xs: 6, md: 7 },
                height: { xs: 6, md: 7 },
                borderRadius: "50%",
                bgcolor: `${color}.main`,
                boxShadow: `0 0 0 0 ${alpha(theme.palette[color].main, 0.7)}`,
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%": {
                    boxShadow: `0 0 0 0 ${alpha(theme.palette[color].main, 0.7)}`,
                  },
                  "70%": {
                    boxShadow: `0 0 0 4px ${alpha(theme.palette[color].main, 0)}`,
                  },
                  "100%": {
                    boxShadow: `0 0 0 0 ${alpha(theme.palette[color].main, 0)}`,
                  },
                },
              }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default CompactMetricCard;